import { basePath } from "../settings";

export function throttle(func: Function, ms: number = 2000): Function {
    // reference: https://javascript.info/task/throttle

    let isThrottled = false;
    let savedArgs: any;
    let savedThis: any;
  
    function wrapper() {
  
        if (isThrottled) { // (2)
            savedArgs = arguments;
            // @ts-ignore
            savedThis = this;
            return;
        }
        isThrottled = true;
    
        // @ts-ignore
        func.apply(this, arguments); // (1)
    
        setTimeout(function() {
            isThrottled = false; // (3)
            if (savedArgs) {
            wrapper.apply(savedThis, savedArgs);
            savedArgs = savedThis = null;
            }
        }, ms);
    }
  
    return wrapper;
}

export function debounce<T extends any[]>(func: (...args: T) => void, delay: number): ((...args: T) => void)[] {
    let currTimeout: ReturnType<typeof setTimeout> | null = null;
    return [
        function (this: any) {
            const context = this;
            const args = arguments as unknown as T;
            if (currTimeout !== null) {
                clearTimeout(currTimeout);
            }
            currTimeout = setTimeout(() => func.apply(context, args), delay);
        },
        function (this: any) {
            const context = this;
            const args = arguments as unknown as T;
            if (currTimeout !== null) {
                clearTimeout(currTimeout);
            }
            func.apply(context, args);
        }
    ];
}

export function countSeqsInFasta(fasta: string): number {
    const m = fasta.match(/^>/gm);
    return m ? m.length : 0;
}

export function ll_url(url: string) {
    return basePath + url;
}

export function equalArray<T> (
        foo: Array<T>,
        bar: Array<T>,
        equalItemFn?: (fooItem: T, barItem: T) => boolean
): boolean {
    if (foo === bar) {
        return true;
    }
    if (foo.length !== bar.length) {
        return false;
    }
    if (equalItemFn === undefined) {
        equalItemFn = (fooItem: T, barItem: T) => fooItem === barItem;
    }
    const len = foo.length;
    for (let i = 0; i < len; i++) {
        if (equalItemFn(foo[i], bar[i]) === false) {
            return false;
        }
    }
    return true;
}

export function equalArrayUnsorted<T>(foo: Array<T>, bar: Array<T>): boolean {
    return (
        foo === bar ||
        equalArray(
            foo.slice().sort(),
            bar.slice().sort(),
        )
    );
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
export function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function findDuplicates(arr: string[]): string[] {
    const itemToCount: Record<string, number> = arr.reduce((result, item) => {
        result[item] = (result[item] || 0) + 1;
        return result;
    }, {});
    const duplicates = Object.entries(itemToCount).filter(e => e[1] > 1).map(e => e[0]);
    duplicates.sort();
    return duplicates;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
export function intersection<T>(setA: Set<T>, setB: Set<T>): Set<T> {
    let _intersection: Set<T> = new Set();
    for (let elem of setB) {
        if (setA.has(elem)) {
            _intersection.add(elem);
        }
    }
    return _intersection;
}

export function union<T>(setA: Set<T>, setB: Set<T>): Set<T> {
    let _union: Set<T> = new Set(setA);
    for (let elem of setB) {
        _union.add(elem);
    }
    return _union;
}

export function splitByPrefix(text: string, prefixes: string[]): string[] {
    const indexes = prefixes.map(prefix => text.indexOf(prefix, 1)).filter(idx => idx >= 0);
    if (indexes.length == 0) {
        return [ text ];
    }
    const idx = Math.min(...indexes);
    return [
        text.slice(0, idx),
        ...splitByPrefix(text.slice(idx), prefixes)
    ];
}

export function splitSentences(text: string): string[] {
    return text.split('. ').filter(s => s.length > 0).map(s => (s[s.length-1] != ".") ? (s + ".") : s);
}

/**
 * Group items by key function
 * @param xs - items
 * @param key - key function
 */
export function groupBy<T>(xs: T[], key: (x: T) => string): Record<string, T[]> {
    return xs.reduce((rv, x: T) => {
        const v = key(x);
        (rv[v] ??= []).push(x);
        return rv;
    }, {});
}

export function comparator<T>(key: keyof T | ((t: T) => (string | number)), reverse: boolean = false): (a: T, b: T) => number {
    if (typeof key === 'function') {
        return (a, b) => {
            let cmp: number;
            if (a === b || key(a) === key(b)) {
                cmp = 0;
            } else {
                cmp = key(a) < key(b) ? -1 : 1;
            }
            if (reverse) {
                cmp = cmp * -1;
            }
            return cmp;
        }
    } else {
        return comparator(t => (t[key] as any), reverse);
    }
}
